'use client';

import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import { processClassName } from '@/app/_lens-components/style/responsive';
import Image from '@/components/Image';
import { EPrescriptionType } from '@/utils/enums/goods';
import { useMediaQuery } from '@mui/material';
import { NewCloseOutlined, WapNavBackOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useEffect, useMemo, useState } from 'react';

interface HeaderProps {
  onLeftBack: () => void;
  onClose?: () => void;
  isFrameProcess: boolean;
  inDialog?: boolean;
}

export default function Header({ onLeftBack, onClose, isFrameProcess, inDialog }: HeaderProps) {
  const {
    nowStep,
    // prescriptionScrollTop,
    // setPrescriptionScrollTop,
    prescriptionType,
    selectedColorItem,
  } = useLensProcessStore();
  const isWap = useMediaQuery('(max-width:1280px)');
  const [prescriptionScrollTop, setPrescriptionScrollTop] = useState(0);
  const t = useTranslations('lens-process');
  const shrinkHeight = 83;
  const oneFrameOnlyFlag = useMemo(() => {
    return prescriptionType === EPrescriptionType.FRAME_ONLY;
  }, [prescriptionType]);
  const innerNowStep = useMemo(() => {
    if (!isFrameProcess) {
      const isNeedAddOne =
        [EPrescriptionType.READERS, EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION].includes(
          prescriptionType as EPrescriptionType,
        ) && isWap;
      if (
        (prescriptionType === EPrescriptionType.NON_PRESCRIPTION && nowStep >= 1) ||
        isNeedAddOne
      ) {
        // 原站没有此逻辑，这里不做更改
        // if (selectedColorItem?.is_coating === 2 && nowStep >= 1) {
        //   return nowStep + 2;
        // }
        return nowStep + 1;
      }
    }
    if (isFrameProcess) {
      if (prescriptionType === EPrescriptionType.FRAME_ONLY || selectedColorItem?.is_coating === 2)
        return nowStep + 1;
    }
    return nowStep;
  }, [isFrameProcess, isWap, nowStep, prescriptionType, selectedColorItem]);
  const totalSteps = isFrameProcess ? 2 : 4;
  const currentStep = Math.min(Math.max(innerNowStep + 1, 1), totalSteps);
  const progressText = t('stepProgress', { current: currentStep, total: totalSteps });
  const progressProps = {
    role: 'progressbar' as const,
    'aria-label': t('selectLenses'),
    'aria-valuemin': 1,
    'aria-valuemax': totalSteps,
    'aria-valuenow': currentStep,
    'aria-valuetext': progressText,
  };
  useEffect(() => {
    const dialogContent = document.querySelector('.lens-process-dialog-content');
    if (inDialog && !dialogContent) {
      return;
    }
    const handleScroll = () => {
      if (inDialog && dialogContent) {
        setPrescriptionScrollTop(dialogContent.scrollTop);
      } else {
        setPrescriptionScrollTop(window.scrollY);
      }
    };

    if (isWap) {
      if (inDialog && dialogContent) {
        dialogContent.addEventListener('scroll', handleScroll, { passive: true });
      } else {
        document.addEventListener('scroll', handleScroll, { passive: true });
      }
      return () => {
        if (inDialog && dialogContent) {
          dialogContent.removeEventListener('scroll', handleScroll);
        } else {
          document.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [inDialog, isWap]);

  const leftBtn = useMemo(() => {
    return (
      <div
        className={
          nowStep !== 0 ? 'text-[#333333] text-[20px] xl:text-[22px] font-bold' : 'invisible'
        }
      >
        <button
          type="button"
          onClick={onLeftBack}
          className="flex items-center cursor-pointer border-0 bg-transparent p-0"
          aria-label={t('back')}
        >
          <WapNavBackOutlined className="text-[24px]" aria-hidden />
          <span className="hidden xl:block xl:text-[16px]">{t('back')}</span>
        </button>
      </div>
    );
  }, [nowStep, onLeftBack, t]);
  const rightBtn = useMemo(() => {
    return (
      <div className="text-[#333333] text-[24px] xl:text-[24px]">
        <button
          type="button"
          className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-0"
          onClick={onClose}
          aria-label={t('closeLensSelection')}
        >
          <NewCloseOutlined className="cursor-pointer" aria-hidden />
        </button>
      </div>
    );
  }, [onClose]);
  return (
    <>
      {/* 滚动高度大于原进度条高度时，用长条形进度条 */}
      {(prescriptionScrollTop >= shrinkHeight || !isWap) && (
        <div
          {...progressProps}
          className="fixed top-0 left-0 z-[110] w-full h-[5px] flex  overflow-hidden"
        >
          <div className={`left-part ${processClassName.leftPart}`} />
          <div className={`right-part ${processClassName.rightPart} bg-[#d0d4db] h-[5px]`}>
            {!isFrameProcess ? (
              <div
                className="absolute top-0 left-0 h-[5px] bg-primary-purple"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            ) : (
              <div
                className="absolute top-0 left-0 h-[5px] bg-primary-purple"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            )}
          </div>
        </div>
      )}

      {prescriptionScrollTop < shrinkHeight && (
        <div className="max-xl:fixed top-0 left-0 right-0 z-[110] bg-[#f4f4f4] xl:bg-[#f8f8f8] h-[44px] max-xl:px-[16px] xl:mx-[-2px] xl:mb-[13px] flex justify-between items-center box-border">
          {leftBtn}
          <div className="flex justify-center items-center xl:hidden" {...progressProps}>
            {/* Select Lens 四步 */}
            {!isFrameProcess ? (
              <div className="flex justify-center items-center">
                {/* 第一步 */}
                <Image
                  src={
                    innerNowStep === 0
                      ? '/goods-detail/continue2.png'
                      : '/goods-detail/selected2.png'
                  }
                  alt=""
                  width={18}
                  height={18}
                  className="w-[18px] h-[18px]"
                />
                {/* 第一步划线 */}
                <div className="relative w-[40px] h-[2px]">
                  <span
                    className={`absolute left-[7px] top-0 w-[26px] h-[2px] rounded-[2px] ${innerNowStep > 0 ? 'bg-[#cfdafc]' : 'bg-[#dce1e5]'}`}
                  />
                </div>
                {/* 第二步 */}
                {innerNowStep <= 0 ? (
                  <Image
                    src="/goods-detail/unreached2.png"
                    alt=""
                    width={18}
                    height={18}
                    className="w-[18px] h-[18px]"
                  />
                ) : (
                  <Image
                    src={
                      innerNowStep === 1
                        ? '/goods-detail/continue2.png'
                        : '/goods-detail/selected2.png'
                    }
                    alt=""
                    width={18}
                    height={18}
                    className="w-[18px] h-[18px]"
                  />
                )}
                {/* 第二步划线 */}
                <div className="relative w-[40px] h-[2px]">
                  <span
                    className={`absolute left-[7px] top-0 w-[26px] h-[2px] rounded-[2px] ${innerNowStep > 1 ? 'bg-[#cfdafc]' : 'bg-[#dce1e5]'}`}
                  />
                </div>
                {/* 第三步 */}
                {innerNowStep <= 1 ? (
                  <Image
                    src="/goods-detail/unreached2.png"
                    alt=""
                    width={18}
                    height={18}
                    className="w-[18px] h-[18px]"
                  />
                ) : (
                  <Image
                    src={
                      innerNowStep === 2
                        ? '/goods-detail/continue2.png'
                        : '/goods-detail/selected2.png'
                    }
                    alt=""
                    width={18}
                    height={18}
                    className="w-[18px] h-[18px]"
                  />
                )}
                {/* 第三步划线 */}
                <div className="relative w-[40px] h-[2px]">
                  <span
                    className={`absolute left-[7px] top-0 w-[26px] h-[2px] rounded-[2px] ${innerNowStep > 2 ? 'bg-[#cfdafc]' : 'bg-[#dce1e5]'}`}
                  />
                </div>
                {/* 第四步 */}
                {innerNowStep <= 2 ? (
                  <Image
                    src="/goods-detail/unreached2.png"
                    alt=""
                    width={18}
                    height={18}
                    className="w-[18px] h-[18px]"
                  />
                ) : (
                  <Image
                    src="/goods-detail/continue2.png"
                    alt=""
                    width={18}
                    height={18}
                    className="w-[18px] h-[18px]"
                  />
                )}
              </div>
            ) : (
              /* Frame Only 两步 */
              <div className="flex justify-center items-center">
                {!oneFrameOnlyFlag ? (
                  <>
                    {/* 第一步 */}
                    <Image
                      src={
                        innerNowStep === 0
                          ? '/goods-detail/continue2.png'
                          : '/goods-detail/selected2.png'
                      }
                      alt=""
                      width={18}
                      height={18}
                      className="w-[18px] h-[18px]"
                    />
                    {/* 第一步划线 */}
                    <div className="relative w-[40px] h-[2px]">
                      <span
                        className={`absolute left-[7px] top-0 w-[26px] h-[2px] rounded-[2px] ${innerNowStep === 0 ? 'bg-[#dce1e5]' : 'bg-[#cfdafc]'}`}
                      />
                    </div>
                    {/* 第二步 */}
                    {innerNowStep <= 0 ? (
                      <Image
                        src="/goods-detail/unreached2.png"
                        alt=""
                        width={18}
                        height={18}
                        className="w-[18px] h-[18px]"
                      />
                    ) : (
                      <Image
                        src="/goods-detail/continue2.png"
                        alt=""
                        width={18}
                        height={18}
                        className="w-[18px] h-[18px]"
                      />
                    )}
                  </>
                ) : (
                  <>
                    {/* 直接选择Frame Only选项 都显示完成 */}
                    <Image
                      src="/goods-detail/selected2.png"
                      alt=""
                      width={18}
                      height={18}
                      className="w-[18px] h-[18px]"
                    />
                    <div className="relative w-[40px] h-[2px]">
                      <span className="absolute left-[7px] top-0 w-[26px] h-[2px] rounded-[2px] bg-[#cfdafc]" />
                    </div>
                    <Image
                      src="/goods-detail/selected2.png"
                      alt=""
                      width={18}
                      height={18}
                      className="w-[18px] h-[18px]"
                    />
                  </>
                )}
              </div>
            )}
          </div>
          {rightBtn}
        </div>
      )}
    </>
  );
}
